import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios'; // Import Axios for HTTP requests
import defaultMarkerIconUrl from '../assets/images/marker-red.png';
import { getTileLayerConfig, getDefaultMapView } from '../util/mapProvider';

const LeafletSingleMap = ({
  mapHeight,
  center,
  zoom,
  setFormData,
  markerIconUrl = defaultMarkerIconUrl,
}) => {
  const generalSettingLists = useSelector((state) => state.generalSettingReducer.generalSettingLists);
  const mapProvider = generalSettingLists?.mapProvider;
  const tileLayerConfig = getTileLayerConfig(mapProvider);
  const defaultMapView = useMemo(() => getDefaultMapView(generalSettingLists), [generalSettingLists]);
  const effectiveCenter = center || defaultMapView.center;
  const effectiveZoom = zoom || defaultMapView.zoom;

  const [draggable, setDraggable] = useState(true);
  const [position, setPosition] = useState(effectiveCenter); // Initialize position with center prop
  const markerRef = useRef(null);

  const markerIcon = useMemo(
    () =>
      L.icon({
        iconUrl: markerIconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      }),
    [markerIconUrl]
  );

  // Update position state when center prop changes
  useEffect(() => {
    setPosition(effectiveCenter);
  }, [effectiveCenter]);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const newPosition = marker.getLatLng();
          setPosition([newPosition.lat, newPosition.lng]);
          setFormData(prevData => ({
            ...prevData,
            latitude: parseFloat(newPosition.lat).toFixed(8),
            longitude: parseFloat(newPosition.lng).toFixed(8),
          }));
          getAddressFromCoords(parseFloat(newPosition.lat).toFixed(8), parseFloat(newPosition.lng).toFixed(8));
        }
      },
    }),
    [setFormData]
  );

  const getAddressFromCoords = async (lat, lng) => {
    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      setFormData(prevData => ({
        ...prevData,
        address: response.data.display_name,
      }));
    } catch (error) {
    }
  };

  const MapUpdater = () => {
    const map = useMap();
    useEffect(() => {
      map.setView(position, effectiveZoom); // Set initial view with position and zoom
    }, [position, effectiveZoom, map]);
    return null;
  };

  return (
    <div style={{ height: mapHeight }}>
      <MapContainer center={effectiveCenter} zoom={effectiveZoom} style={{ height: '100%' }}>
        <MapUpdater />
        <TileLayer
          key={mapProvider}
          url={tileLayerConfig.url}
          subdomains={tileLayerConfig.subdomains}
          attribution={tileLayerConfig.attribution}
          maxZoom={tileLayerConfig.maxZoom}
        />
        <Marker
          draggable={draggable}
          eventHandlers={eventHandlers}
          position={position}
          ref={markerRef}
          icon={markerIcon}
        />
      </MapContainer>
    </div>
  );
};

export default LeafletSingleMap;
